import {
  type CSSProperties,
  type FC,
  useEffect,
  useRef,
  useState,
} from "react";

import { ArrowRight, X } from "lucide-react";
import { createPortal } from "react-dom";

import { Button } from "@rook/ui";

import { type TourStep, type TourStepSide } from "./sidebarTour";

type Geom = {
  top: number;
  left: number;
  width: number;
  height: number;
};

type Position = {
  top: number;
  left: number;
  arrowSide: TourStepSide;
};

type GuidedTourProps = {
  active: boolean;
  steps: TourStep[];
  currentStep: number;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
};

const SPOTLIGHT_PADDING = 6;
const TOOLTIP_WIDTH = 340;
const TOOLTIP_GUTTER = 16;
const INITIAL_TOOLTIP_HEIGHT = 200;

// ─── Position computation ────────────────────────────────────────

const SIDES: TourStepSide[] = ["right", "bottom", "left", "top"];

const buildSideOrder = (preferred?: TourStepSide): TourStepSide[] => {
  if (!preferred) return SIDES;
  return [preferred, ...SIDES.filter((s) => s !== preferred)];
};

const fitsRight = (g: Geom, vw: number): boolean =>
  g.left + g.width + TOOLTIP_GUTTER + TOOLTIP_WIDTH < vw;
const fitsLeft = (g: Geom): boolean =>
  g.left - TOOLTIP_GUTTER - TOOLTIP_WIDTH > 0;
const fitsBottom = (g: Geom, vh: number, h: number): boolean =>
  g.top + g.height + TOOLTIP_GUTTER + h < vh;
const fitsTop = (g: Geom, h: number): boolean =>
  g.top - TOOLTIP_GUTTER - h > 0;

const sideFits = (
  side: TourStepSide,
  g: Geom,
  vw: number,
  vh: number,
  h: number,
): boolean => {
  if (side === "right") return fitsRight(g, vw);
  if (side === "left") return fitsLeft(g);
  if (side === "bottom") return fitsBottom(g, vh, h);
  return fitsTop(g, h);
};

const clampHorizontal = (left: number, vw: number): number => {
  const max = vw - TOOLTIP_WIDTH - TOOLTIP_GUTTER;
  if (left < TOOLTIP_GUTTER) return TOOLTIP_GUTTER;
  if (left > max) return max;
  return left;
};

const clampVertical = (top: number, vh: number, h: number): number => {
  const max = vh - h - TOOLTIP_GUTTER;
  if (top < TOOLTIP_GUTTER) return TOOLTIP_GUTTER;
  if (top > max) return max;
  return top;
};

const positionForSide = (
  side: TourStepSide,
  g: Geom,
  vw: number,
  vh: number,
  h: number,
): Position => {
  if (side === "right") {
    return {
      top: clampVertical(g.top + g.height / 2 - h / 2, vh, h),
      left: g.left + g.width + TOOLTIP_GUTTER,
      arrowSide: "left",
    };
  }
  if (side === "left") {
    return {
      top: clampVertical(g.top + g.height / 2 - h / 2, vh, h),
      left: g.left - TOOLTIP_GUTTER - TOOLTIP_WIDTH,
      arrowSide: "right",
    };
  }
  if (side === "top") {
    return {
      top: g.top - TOOLTIP_GUTTER - h,
      left: clampHorizontal(g.left + g.width / 2 - TOOLTIP_WIDTH / 2, vw),
      arrowSide: "bottom",
    };
  }
  return {
    top: g.top + g.height + TOOLTIP_GUTTER,
    left: clampHorizontal(g.left + g.width / 2 - TOOLTIP_WIDTH / 2, vw),
    arrowSide: "top",
  };
};

const computePosition = (
  g: Geom,
  preferredSide: TourStepSide | undefined,
  vw: number,
  vh: number,
  tooltipH: number,
): Position => {
  const order = buildSideOrder(preferredSide);
  const chosen =
    order.find((side) => sideFits(side, g, vw, vh, tooltipH)) ?? "bottom";
  return positionForSide(chosen, g, vw, vh, tooltipH);
};

// ─── Spotlight (4 backdrop panels + ring around target) ──────────

const SpotlightOverlay: FC<{ geom: Geom | null }> = ({ geom }) => {
  if (!geom) {
    return (
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-[1px] pointer-events-none"
        aria-hidden
      />
    );
  }
  const t = geom.top - SPOTLIGHT_PADDING;
  const l = geom.left - SPOTLIGHT_PADDING;
  const w = geom.width + SPOTLIGHT_PADDING * 2;
  const h = geom.height + SPOTLIGHT_PADDING * 2;
  const backdrop =
    "absolute bg-black/60 backdrop-blur-[1px] pointer-events-none";

  return (
    <>
      <div
        className={`${backdrop} left-0 right-0 top-0`}
        style={{ height: t }}
        aria-hidden
      />
      <div
        className={`${backdrop} left-0 right-0 bottom-0`}
        style={{ top: t + h }}
        aria-hidden
      />
      <div
        className={`${backdrop} top-0 left-0`}
        style={{ top: t, height: h, width: l }}
        aria-hidden
      />
      <div
        className={`${backdrop} top-0`}
        style={{ top: t, height: h, left: l + w, right: 0 }}
        aria-hidden
      />
      <div
        className="absolute pointer-events-none rounded-md ring-2 ring-rook-purple ring-offset-2 ring-offset-transparent"
        style={{ top: t, left: l, width: w, height: h }}
        aria-hidden
      />
    </>
  );
};

// ─── Arrow tail (CSS triangle, color matches dashboard-surface) ──

const arrowStyleBySide: Record<TourStepSide, CSSProperties> = {
  top: {
    top: -6,
    left: "calc(50% - 6px)",
    borderWidth: "0 6px 6px 6px",
    borderColor:
      "transparent transparent var(--dashboard-surface) transparent",
  },
  bottom: {
    bottom: -6,
    left: "calc(50% - 6px)",
    borderWidth: "6px 6px 0 6px",
    borderColor:
      "var(--dashboard-surface) transparent transparent transparent",
  },
  left: {
    top: 24,
    left: -6,
    borderWidth: "6px 6px 6px 0",
    borderColor: "transparent var(--dashboard-surface) transparent transparent",
  },
  right: {
    top: 24,
    right: -6,
    borderWidth: "6px 0 6px 6px",
    borderColor: "transparent transparent transparent var(--dashboard-surface)",
  },
};

const ArrowIndicator: FC<{ side: TourStepSide }> = ({ side }) => (
  <div
    className="absolute h-0 w-0"
    style={{ ...arrowStyleBySide[side], borderStyle: "solid" }}
    aria-hidden
  />
);

// ─── Tooltip card ───────────────────────────────────────────────

type TooltipCardProps = {
  step: TourStep;
  stepIndex: number;
  totalSteps: number;
  position: Position;
  onSkip: () => void;
  onPrev: () => void;
  onNext: () => void;
  onMeasure: (height: number) => void;
};

const TooltipCard: FC<TooltipCardProps> = ({
  step,
  stepIndex,
  totalSteps,
  position,
  onSkip,
  onPrev,
  onNext,
  onMeasure,
}) => {
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (cardRef.current) {
      onMeasure(cardRef.current.offsetHeight);
    }
  }, [onMeasure, step]);

  return (
    <div
      ref={cardRef}
      className="absolute pointer-events-auto rounded-xl border border-white/10 bg-rook-home shadow-2xl"
      style={{ top: position.top, left: position.left, width: TOOLTIP_WIDTH }}
    >
      <ArrowIndicator side={position.arrowSide} />

      <div className="flex items-start justify-between gap-2 px-5 pt-4 pb-2">
        <div className="space-y-1">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-rook-olive">
            {`Step ${stepIndex + 1} of ${totalSteps}`}
          </div>
          <h2
            id="guided-tour-title"
            className="text-base font-semibold tracking-tight text-rook-stone"
          >
            {step.title}
          </h2>
        </div>
        <Button
          type="button"
          variant={null}
          size={null}
          onClick={onSkip}
          aria-label="Skip tour"
          className="rounded p-1 text-rook-olive transition hover:bg-white/[0.06] hover:text-rook-stone"
        >
          <X className="h-4 w-4" aria-hidden />
        </Button>
      </div>

      <p className="px-5 pb-4 text-sm text-rook-olive">{step.body}</p>

      <div className="flex items-center justify-between gap-2 border-t border-white/[0.06] px-4 py-2.5">
        <Button
          type="button"
          variant={null}
          size={null}
          onClick={onSkip}
          className="rounded-lg px-3 py-1.5 text-sm font-medium text-rook-olive transition hover:bg-white/[0.06] hover:text-rook-stone"
        >
          Skip
        </Button>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={null}
            size={null}
            onClick={onPrev}
            disabled={isFirst}
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-rook-olive transition hover:bg-white/[0.06] hover:text-rook-stone disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-rook-olive"
          >
            Back
          </Button>
          <Button
            type="button"
            variant={null}
            size={null}
            onClick={onNext}
            className="inline-flex items-center gap-1.5 rounded-lg bg-rook-purple px-3.5 py-1.5 text-sm font-medium text-white transition hover:brightness-110"
          >
            {isLast ? "Done" : "Next"}
            {isLast ? null : <ArrowRight className="h-4 w-4" aria-hidden />}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Outer GuidedTour component ─────────────────────────────────

export const GuidedTour: FC<GuidedTourProps> = ({
  active,
  steps,
  currentStep,
  onClose,
  onNext,
  onPrev,
}) => {
  const [geom, setGeom] = useState<Geom | null>(null);
  const [position, setPosition] = useState<Position | null>(null);
  const [tooltipHeight, setTooltipHeight] = useState(INITIAL_TOOLTIP_HEIGHT);

  // Recompute target geometry on step change + window resize/scroll.
  useEffect(() => {
    if (!active) return undefined;
    const step = steps[currentStep];
    if (!step) return undefined;

    const update = () => {
      const target = document.querySelector(step.selector);
      if (!target) {
        setGeom(null);
        setPosition(null);
        return;
      }
      const r = target.getBoundingClientRect();
      const g: Geom = {
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
      };
      setGeom(g);
      setPosition(
        computePosition(
          g,
          step.preferredSide,
          window.innerWidth,
          window.innerHeight,
          tooltipHeight,
        ),
      );
      // Soft-scroll into view if the target is offscreen.
      target.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    };

    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [active, currentStep, steps, tooltipHeight]);

  // ESC closes the tour.
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, onClose]);

  if (!active || typeof document === "undefined") return null;
  const step = steps[currentStep];
  if (!step) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="guided-tour-title"
      className="fixed inset-0 z-[100]"
    >
      <SpotlightOverlay geom={geom} />
      {geom && position ? (
        <TooltipCard
          step={step}
          stepIndex={currentStep}
          totalSteps={steps.length}
          position={position}
          onSkip={onClose}
          onPrev={onPrev}
          onNext={onNext}
          onMeasure={setTooltipHeight}
        />
      ) : null}
    </div>,
    document.body,
  );
};
